import { endOfMonth, format, startOfMonth } from "date-fns";
import { useCurrentFarmAnimalGroups, useFields } from "model";
import { getAllFieldsForAnimalGroupIdFromArray } from "model/fieldHelpers";
import { useStockClassesByIds } from "model/useStockClassQueries";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  FeedBudgetPlanListItem,
  FeedBudgetPlanWithVersion,
} from "services";
import {
  activateFeedBudgetPlan,
  createFeedBudgetPlan,
  createStockClass,
  deleteFeedBudgetPlan,
  deleteStockClass,
  getFeedBudgetPlan,
  listFeedBudgetPlans,
  updateFeedBudgetPlan,
  updateFeedBudgetPlanAnimalGroupBudget,
} from "services";
import { useAppStore } from "stores";
import { v4 as uuid } from "uuid";
import {
  createDefaultGrazingMonths,
  createDefaultSupplementPlans,
  DEFAULT_INITIAL_PASTURE_COVER,
  DEFAULT_SUPPLEMENT_FEED_TYPES,
  DEFAULT_SUPPLEMENT_SUBSTITUTION_RATES,
  DEFAULT_TARGET_RESIDUAL,
  TYPICAL_ALLOCATIONS,
  TYPICAL_WEIGHTS,
} from "./constants";
import type {
  FeedBudgetPlan,
  StockClassMonthEntry,
  StockClassMonthlyPlan,
} from "./types";

function deepClone<T>(value: T): T {
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
}

/**
 * Ensures every stock class in a multi-year plan has year-1 entries.
 * If a stock class only has year-0 entries and the plan spans 2+ years,
 * copies those entries to year-1 so the second year shows populated data.
 * Returns the (possibly mutated) plan and the IDs of groups that were changed.
 */
function normalizeMultiYearPlan(plan: FeedBudgetPlanWithVersion): {
  plan: FeedBudgetPlanWithVersion;
  dirtyGroupIds: string[];
  metadataDirty: boolean;
} {
  const startYear = plan.startDate
    ? new Date(plan.startDate).getFullYear()
    : null;
  const endYear = plan.endDate ? new Date(plan.endDate).getFullYear() : null;
  if (!startYear || !endYear || endYear <= startYear) {
    return { plan, dirtyGroupIds: [], metadataDirty: false };
  }

  // Backfill year-1 grazing months (growth rate + utilisation) by copying year-0
  const grazingHasYear1 = plan.grazingMonths.some(
    (gm) => (gm.yearOffset ?? 0) === 1
  );
  const grazingMonths = grazingHasYear1
    ? plan.grazingMonths
    : [
        ...plan.grazingMonths,
        ...plan.grazingMonths
          .filter((gm) => (gm.yearOffset ?? 0) === 0)
          .map((gm) => ({ ...gm, yearOffset: 1 })),
      ];

  // Backfill year-1 stock class entries by copying year-0
  const dirtyGroupIds: string[] = [];
  const animalGroupBudgets = plan.animalGroupBudgets.map((agb) => {
    const needsYear1 = agb.stockClassPlans.some(
      (sc) => !sc.months.some((m) => (m.yearOffset ?? 0) === 1)
    );
    if (!needsYear1) return agb;

    dirtyGroupIds.push(agb.animalGroupId);
    return {
      ...agb,
      stockClassPlans: agb.stockClassPlans.map((sc) => {
        const hasYear1 = sc.months.some((m) => (m.yearOffset ?? 0) === 1);
        if (hasYear1) return sc;
        const year0Months = sc.months.filter((m) => (m.yearOffset ?? 0) === 0);
        const year1Months: StockClassMonthEntry[] = year0Months.map((m) => ({
          ...m,
          yearOffset: 1,
        }));
        return { ...sc, months: [...sc.months, ...year1Months] };
      }),
    };
  });

  const metadataDirty = !grazingHasYear1;
  return {
    plan: { ...plan, grazingMonths, animalGroupBudgets },
    dirtyGroupIds,
    metadataDirty,
  };
}

export function useFeedBudgetPlan() {
  const farmId = useAppStore((s) => s.currentFarmId);
  const currentYear = new Date().getFullYear();
  const [farmPlansList, setFarmPlansList] = useState<FeedBudgetPlanListItem[]>(
    []
  );
  const [plan, setPlan] = useState<FeedBudgetPlanWithVersion | null>(null);
  const [draftPlan, setDraftPlan] = useState<FeedBudgetPlanWithVersion | null>(
    null
  );
  const [activePlanId, setActivePlanIdState] = useState<string | null>(null);
  const [viewedPlanId, setViewedPlanIdState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [metadataDirty, setMetadataDirty] = useState(false);
  const [dirtyGroups, setDirtyGroups] = useState<Set<string>>(new Set());
  // Always-current ref to the last-saved plan — used by discardGroupChanges to
  // avoid stale closure issues that arise when plan is captured in useCallback.
  const savedPlanRef = useRef<FeedBudgetPlanWithVersion | null>(null);
  savedPlanRef.current = plan;

  // Cache a just-created plan so refreshViewedPlan can skip the redundant fetch.
  const freshPlanRef = useRef<FeedBudgetPlanWithVersion | null>(null);

  // Get existing farm data
  const { data: animalGroups = [] } = useCurrentFarmAnimalGroups();
  const { data: allFields = [] } = useFields();

  const allStockClassIds = useMemo(
    () => animalGroups.flatMap((ag) => ag.stockClasses),
    [animalGroups]
  );
  const { data: stockClasses = [] } = useStockClassesByIds(allStockClassIds);

  const effectiveFarmId = farmId ?? "";

  const refreshPlansList = useCallback(async () => {
    if (!effectiveFarmId) return;
    setIsLoading(true);
    try {
      const list = await listFeedBudgetPlans(effectiveFarmId);
      // Preserve stable order: keep existing positions, append new plans at end
      setFarmPlansList((prev) => {
        const retained = prev
          .filter((p) => list.some((l) => l.guid === p.guid))
          // find is guaranteed non-null here because we filtered for it above
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          .map((p) => list.find((l) => l.guid === p.guid)!);
        const added = list.filter((l) => !prev.some((p) => p.guid === l.guid));
        return [...retained, ...added];
      });
      const active = list.find((p) => p.isActive)?.guid ?? null;
      setActivePlanIdState(active);
      const validViewed =
        viewedPlanId && list.some((p) => p.guid === viewedPlanId);
      const nextViewed = validViewed
        ? viewedPlanId
        : active ?? list[0]?.guid ?? null;
      setViewedPlanIdState(nextViewed);
    } finally {
      setIsLoading(false);
    }
  }, [effectiveFarmId, viewedPlanId]);

  const refreshViewedPlan = useCallback(async () => {
    if (!viewedPlanId) {
      setPlan(null);
      setDraftPlan(null);
      return;
    }
    // If we have a freshly-created plan for this id, use it directly — no refetch needed.
    if (freshPlanRef.current?.guid === viewedPlanId) {
      const fresh = freshPlanRef.current;
      freshPlanRef.current = null;
      const {
        plan: normalized,
        dirtyGroupIds,
        metadataDirty: normMeta,
      } = normalizeMultiYearPlan(fresh);
      setPlan(normalized);
      setDraftPlan(deepClone(normalized));
      setMetadataDirty(normMeta);
      setDirtyGroups(new Set(dirtyGroupIds));
      setSaveError(null);
      return;
    }
    setIsLoading(true);
    try {
      const full = await getFeedBudgetPlan(viewedPlanId);
      const {
        plan: normalized,
        dirtyGroupIds,
        metadataDirty: normMeta,
      } = normalizeMultiYearPlan(full);
      setPlan(normalized);
      setDraftPlan(deepClone(normalized));
      setMetadataDirty(normMeta);
      setDirtyGroups(new Set(dirtyGroupIds));
      setSaveError(null);
    } finally {
      setIsLoading(false);
    }
  }, [viewedPlanId]);

  useEffect(() => {
    void refreshPlansList();
  }, [refreshPlansList]);

  useEffect(() => {
    void refreshViewedPlan();
  }, [refreshViewedPlan]);

  // Keep grazingAreaHa in sync with current field assignments.
  // Runs whenever fields or the draft plan change; only updates the current
  // month override where the area has actually changed and marks groups dirty.
  useEffect(() => {
    if (!draftPlan || allFields.length === 0) return;
    const currentMonth = new Date().getMonth();

    const changedIds: string[] = [];
    const updatedBudgets = draftPlan.animalGroupBudgets.map((agb) => {
      const groupFields = getAllFieldsForAnimalGroupIdFromArray(
        allFields,
        agb.animalGroupId
      );
      const newAreaHa =
        Math.round(
          groupFields.reduce((sum, f) => sum + (f.area || 0) / 10000, 0) * 100
        ) / 100;
      const currentMonthArea =
        agb.monthlyGrazingAreaHa?.[currentMonth] ?? agb.grazingAreaHa;
      if (Math.abs(newAreaHa - currentMonthArea) > 0.001) {
        changedIds.push(agb.animalGroupId);
        return {
          ...agb,
          monthlyGrazingAreaHa: {
            ...(agb.monthlyGrazingAreaHa ?? {}),
            [currentMonth]: newAreaHa,
          },
        };
      }
      return agb;
    });

    if (changedIds.length === 0) return;

    setDraftPlan({ ...draftPlan, animalGroupBudgets: updatedBudgets });
    setDirtyGroups((prev) => {
      const next = new Set(prev);
      changedIds.forEach((id) => next.add(id));
      return next;
    });
  }, [allFields, draftPlan]);

  const setViewedPlanId = useCallback((id: string | null) => {
    setViewedPlanIdState(id);
  }, []);

  const setActivePlanId = useCallback(
    (id: string | null) => {
      if (!id) return;
      void activateFeedBudgetPlan(id).then(() => {
        void refreshPlansList();
      });
    },
    [refreshPlansList]
  );

  const buildDefaultAnimalGroupBudgets = useCallback(
    (startDate?: string, endDate?: string) => {
      const startYear = startDate ? new Date(startDate).getFullYear() : null;
      const endYear = endDate ? new Date(endDate).getFullYear() : null;
      const spans2Years =
        startYear !== null && endYear !== null && endYear > startYear;
      const entryCount = spans2Years ? 24 : 12;

      return animalGroups.map((ag, index) => {
        const groupFields = getAllFieldsForAnimalGroupIdFromArray(
          allFields,
          ag.guid
        );
        const grazingAreaHa = groupFields.reduce(
          (sum, f) => sum + (f.area || 0) / 10000,
          0
        );
        const groupStockClasses = stockClasses.filter(
          (sc) => sc.animalGroup === ag.guid
        );

        const stockClassPlans: StockClassMonthlyPlan[] = groupStockClasses.map(
          (sc) => {
            const typicalAllocation =
              TYPICAL_ALLOCATIONS[sc.defaultStockClassName ?? ""] ?? 2.5;
            const typicalWeight =
              sc.averageAnimalWeightKg > 0
                ? sc.averageAnimalWeightKg
                : TYPICAL_WEIGHTS[sc.defaultStockClassName ?? ""] ?? 65;

            const months: StockClassMonthEntry[] = Array.from(
              { length: entryCount },
              (_, i) => {
                const month = i % 12;
                const entry: StockClassMonthEntry = {
                  month,
                  animalCount: sc.animalCount,
                  averageWeightKg: typicalWeight,
                  allocationPercent: typicalAllocation,
                };
                if (i >= 12) entry.yearOffset = 1;
                return entry;
              }
            );
            return {
              stockClassId: sc.guid,
              stockClassName: sc.name,
              defaultStockClassName: sc.defaultStockClassName ?? undefined,
              months,
            };
          }
        );

        return {
          guid: uuid(),
          animalGroupId: ag.guid,
          animalGroupName: ag.name,
          sortOrder: index,
          grazingAreaHa: Math.round(grazingAreaHa * 100) / 100,
          stockClassPlans,
          supplementFeedTypes: [...DEFAULT_SUPPLEMENT_FEED_TYPES],
          supplementPlans: createDefaultSupplementPlans(),
        };
      });
    },
    [animalGroups, allFields, stockClasses]
  );

  const createPlan = useCallback(
    (options?: { name?: string; startDate?: string; endDate?: string }) => {
      if (!effectiveFarmId) return null;
      const defaultStart = format(
        startOfMonth(new Date(currentYear, 0, 1)),
        "yyyy-MM-dd"
      );
      const defaultEnd = format(
        endOfMonth(new Date(currentYear, 11, 1)),
        "yyyy-MM-dd"
      );

      const startDate = options?.startDate ?? defaultStart;
      const endDate = options?.endDate ?? defaultEnd;
      const startYear = new Date(startDate).getFullYear();

      const animalGroupBudgets = buildDefaultAnimalGroupBudgets(
        startDate,
        endDate
      );
      const nextPlan = {
        farmGuid: effectiveFarmId,
        name: options?.name || `Feed Budget ${startYear}`,
        startDate,
        endDate,
        initialPastureCoverKgHa: DEFAULT_INITIAL_PASTURE_COVER,
        targetResidualKgHa: DEFAULT_TARGET_RESIDUAL,
        grazingMonths: createDefaultGrazingMonths(),
        supplementSubstitutionRates: {
          ...DEFAULT_SUPPLEMENT_SUBSTITUTION_RATES,
        },
        animalGroupBudgets,
      };

      void createFeedBudgetPlan(nextPlan)
        .then((created) => {
          // Cache the full plan so refreshViewedPlan (triggered below) skips the refetch.
          freshPlanRef.current = created;
          // Add to list directly — no need for a round-trip to avoid toggling isLoading.
          setFarmPlansList((prev) => [
            ...prev,
            {
              guid: created.guid,
              farmGuid: effectiveFarmId,
              name: created.name,
              startDate: created.startDate,
              endDate: created.endDate,
              isActive: false,
              version: created.version,
              createdAt: created.createdAt,
              updatedAt: created.updatedAt,
            },
          ]);
          setViewedPlanIdState(created.guid);
        })
        .catch((err) => {
          console.error("[createPlan] API error:", err);
          setSaveError("Failed to create feed budget plan");
        });
      return null;
    },
    [currentYear, effectiveFarmId, buildDefaultAnimalGroupBudgets]
  );

  const duplicatePlan = useCallback(
    (_guid: string) => {
      const source = draftPlan;
      if (!source) return;
      void createFeedBudgetPlan({
        farmGuid: effectiveFarmId,
        name: `${source.name} (copy)`,
        startDate: source.startDate,
        endDate: source.endDate,
        initialPastureCoverKgHa: source.initialPastureCoverKgHa,
        targetResidualKgHa: source.targetResidualKgHa,
        grazingMonths: source.grazingMonths,
        supplementSubstitutionRates: source.supplementSubstitutionRates,
        animalGroupBudgets: source.animalGroupBudgets,
      }).then((created) => {
        setViewedPlanIdState(created.guid);
        setPlan(created);
        setDraftPlan(created);
        void refreshPlansList();
      });
    },
    [draftPlan, effectiveFarmId, refreshPlansList]
  );

  const deletePlan = useCallback(
    (guid: string) => {
      void deleteFeedBudgetPlan(guid).then(() => {
        if (viewedPlanId === guid) {
          setViewedPlanIdState(null);
          setPlan(null);
          setDraftPlan(null);
        }
        void refreshPlansList();
      });
    },
    [refreshPlansList, viewedPlanId]
  );

  /** Update metadata (name, dates) of any plan by ID */
  const updatePlanMetadata = useCallback(
    (
      guid: string,
      metadata: Partial<Pick<FeedBudgetPlan, "name" | "startDate" | "endDate">>
    ) => {
      setDraftPlan((prev) => {
        if (!prev || prev.guid !== guid) return prev;
        return { ...prev, ...metadata };
      });
      setMetadataDirty(true);
    },
    []
  );

  /** Update the currently viewed plan */
  const updatePlan = useCallback(
    (updater: (plan: FeedBudgetPlan) => FeedBudgetPlan) => {
      setDraftPlan((prev) => {
        if (!prev || !viewedPlanId || prev.guid !== viewedPlanId) return prev;
        const next = updater(prev);
        const nextDirty = new Set(dirtyGroups);
        const prevGroups = new Map(
          prev.animalGroupBudgets.map((g) => [g.animalGroupId, g])
        );
        for (const g of next.animalGroupBudgets) {
          const existing = prevGroups.get(g.animalGroupId);
          if (!existing || JSON.stringify(existing) !== JSON.stringify(g)) {
            nextDirty.add(g.animalGroupId);
          }
        }
        if (
          prev.name !== next.name ||
          prev.startDate !== next.startDate ||
          prev.endDate !== next.endDate ||
          prev.initialPastureCoverKgHa !== next.initialPastureCoverKgHa ||
          prev.targetResidualKgHa !== next.targetResidualKgHa ||
          JSON.stringify(prev.grazingMonths) !==
            JSON.stringify(next.grazingMonths) ||
          JSON.stringify(prev.supplementSubstitutionRates) !==
            JSON.stringify(next.supplementSubstitutionRates)
        ) {
          setMetadataDirty(true);
        }
        setDirtyGroups(nextDirty);
        return next;
      });
    },
    [dirtyGroups, viewedPlanId]
  );

  const addStockClassToGroup = useCallback(
    (
      animalGroupId: string,
      name: string,
      defaultStockClassName?: string,
      initialValues?: {
        animalCount?: number;
        weightKg?: number;
        allocationPercent?: number;
        /** Per-month allocation % (12 values). Overrides allocationPercent when provided. */
        allocationByMonth?: number[];
        /** Per-month animal count (12 values). Overrides animalCount when provided. */
        animalCountByMonth?: number[];
      }
    ) => {
      const animalCount = initialValues?.animalCount ?? 0;
      const weightKg =
        initialValues?.weightKg ??
        TYPICAL_WEIGHTS[defaultStockClassName ?? ""] ??
        65;

      const tempId = uuid();

      updatePlan((plan) => {
        // If the plan spans two calendar years, create entries for both years.
        const planStartYear = plan.startDate
          ? new Date(plan.startDate).getFullYear()
          : null;
        const planEndYear = plan.endDate
          ? new Date(plan.endDate).getFullYear()
          : null;
        const planSpans2Years =
          planStartYear !== null &&
          planEndYear !== null &&
          planEndYear > planStartYear;

        // Respect explicit 24-entry animalCountByMonth (spawned classes);
        // otherwise extend to 24 if plan spans 2 years.
        const providedCount = initialValues?.animalCountByMonth?.length ?? 12;
        const entryCount =
          providedCount >= 24 ? providedCount : planSpans2Years ? 24 : 12;

        return {
          ...plan,
          animalGroupBudgets: plan.animalGroupBudgets.map((agb) =>
            agb.animalGroupId === animalGroupId
              ? {
                  ...agb,
                  stockClassPlans: [
                    ...agb.stockClassPlans,
                    {
                      stockClassId: tempId,
                      stockClassName: name,
                      defaultStockClassName,
                      months: Array.from({ length: entryCount }, (_, i) => {
                        const month = i % 12;
                        const entry: StockClassMonthEntry = {
                          month,
                          animalCount:
                            initialValues?.animalCountByMonth?.[i] ??
                            initialValues?.animalCountByMonth?.[i % 12] ??
                            animalCount,
                          averageWeightKg: weightKg,
                          allocationPercent:
                            initialValues?.allocationByMonth?.[month] ??
                            initialValues?.allocationPercent ??
                            TYPICAL_ALLOCATIONS[defaultStockClassName ?? ""] ??
                            2.5,
                        };
                        if (i >= 12) entry.yearOffset = 1;
                        return entry;
                      }),
                    },
                  ],
                }
              : agb
          ),
        };
      });

      void createStockClass({
        name,
        animalGroup: animalGroupId,
        animalCount,
        averageAnimalWeightKg: weightKg,
        consumptionKgPerDay: 0,
        defaultStockClassName,
        birthingPeriods: [],
      })
        .then((stockClass) => {
          updatePlan((plan) => ({
            ...plan,
            animalGroupBudgets: plan.animalGroupBudgets.map((agb) =>
              agb.animalGroupId === animalGroupId
                ? {
                    ...agb,
                    stockClassPlans: agb.stockClassPlans.map((sc) =>
                      sc.stockClassId === tempId
                        ? { ...sc, stockClassId: stockClass.guid }
                        : sc
                    ),
                  }
                : agb
            ),
          }));
        })
        .catch(() => {
          // Backend unavailable — keep the local-only stock class
        });
    },
    [updatePlan]
  );

  const removeStockClassFromGroup = useCallback(
    async (animalGroupId: string, stockClassId: string) => {
      updatePlan((plan) => ({
        ...plan,
        animalGroupBudgets: plan.animalGroupBudgets.map((agb) =>
          agb.animalGroupId === animalGroupId
            ? {
                ...agb,
                stockClassPlans: agb.stockClassPlans.filter(
                  (sc) => sc.stockClassId !== stockClassId
                ),
              }
            : agb
        ),
      }));

      try {
        await deleteStockClass(stockClassId);
      } catch {
        // Stock class may not exist in backend
      }
    },
    [updatePlan]
  );

  const addSupplementFeedType = useCallback(
    (
      animalGroupId: string,
      name: string,
      substitutionRate: number,
      explicitId?: string
    ) => {
      const id = explicitId ?? uuid();
      updatePlan((plan) => ({
        ...plan,
        animalGroupBudgets: plan.animalGroupBudgets.map((agb) =>
          agb.animalGroupId === animalGroupId
            ? {
                ...agb,
                supplementFeedTypes: [
                  ...agb.supplementFeedTypes,
                  { id, name, substitutionRate },
                ],
                supplementPlans: agb.supplementPlans.map((s) => ({
                  ...s,
                  amounts: { ...s.amounts, [id]: 0 },
                })),
              }
            : agb
        ),
      }));
    },
    [updatePlan]
  );

  const removeSupplementFeedType = useCallback(
    (animalGroupId: string, feedTypeId: string) => {
      updatePlan((plan) => ({
        ...plan,
        animalGroupBudgets: plan.animalGroupBudgets.map((agb) => {
          if (agb.animalGroupId !== animalGroupId) return agb;
          return {
            ...agb,
            supplementFeedTypes: agb.supplementFeedTypes.filter(
              (ft) => ft.id !== feedTypeId
            ),
            supplementPlans: agb.supplementPlans.map((s) => {
              const { [feedTypeId]: _removed, ...rest } = s.amounts;
              return { ...s, amounts: rest };
            }),
          };
        }),
      }));
    },
    [updatePlan]
  );

  /** Reset a specific animal group's budget to the last-saved state, discarding local edits. */
  const discardGroupChanges = useCallback((animalGroupId: string) => {
    const saved = savedPlanRef.current;
    if (!saved) return;
    const originalBudget = saved.animalGroupBudgets.find(
      (g) => g.animalGroupId === animalGroupId
    );
    setDraftPlan((draft) => {
      if (!draft) return draft;
      return {
        ...draft,
        grazingMonths: deepClone(saved.grazingMonths),
        animalGroupBudgets: draft.animalGroupBudgets.map((g) =>
          g.animalGroupId === animalGroupId && originalBudget
            ? deepClone(originalBudget)
            : g
        ),
      };
    });
    setDirtyGroups((prev) => {
      const next = new Set(prev);
      next.delete(animalGroupId);
      return next;
    });
    setMetadataDirty(false);
  }, []);

  const savePlan = useCallback(async () => {
    if (!draftPlan || !viewedPlanId) return;
    if (!metadataDirty && dirtyGroups.size === 0) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      let version = plan?.version ?? draftPlan.version;
      if (metadataDirty) {
        const updated = await updateFeedBudgetPlan(viewedPlanId, {
          name: draftPlan.name,
          startDate: draftPlan.startDate,
          endDate: draftPlan.endDate,
          initialPastureCoverKgHa: draftPlan.initialPastureCoverKgHa,
          targetResidualKgHa: draftPlan.targetResidualKgHa,
          grazingMonths: draftPlan.grazingMonths,
          supplementSubstitutionRates: draftPlan.supplementSubstitutionRates,
          version,
        });
        version = updated.version;
      }

      for (const animalGroupId of dirtyGroups) {
        const budget = draftPlan.animalGroupBudgets.find(
          (g) => g.animalGroupId === animalGroupId
        );
        if (!budget?.guid) continue;
        await updateFeedBudgetPlanAnimalGroupBudget(budget.guid, {
          animalGroupName: budget.animalGroupName,
          grazingAreaHa: budget.grazingAreaHa,
          sortOrder: budget.sortOrder,
          monthlyGrazingAreaHa: budget.monthlyGrazingAreaHa as
            | Record<string, number>
            | undefined,
          feedTypeUtilisation: budget.feedTypeUtilisation,
          fieldFeedingUtilisationPercent: budget.fieldFeedingUtilisationPercent,
          stockClassPlans: budget.stockClassPlans,
          supplementFeedTypes: budget.supplementFeedTypes,
          supplementPlans: budget.supplementPlans,
          version,
        });
        version += 1;
      }

      await refreshViewedPlan();
      await refreshPlansList();
    } catch {
      setSaveError("Failed to save feed budget plan");
    } finally {
      setIsSaving(false);
    }
  }, [
    dirtyGroups,
    draftPlan,
    metadataDirty,
    plan?.version,
    refreshPlansList,
    refreshViewedPlan,
    viewedPlanId,
  ]);

  return {
    /** The currently viewed/edited plan */
    plan: draftPlan as FeedBudgetPlan | null,
    farmPlans: farmPlansList.map((p) => ({
      guid: p.guid,
      farm: effectiveFarmId,
      name: p.name,
      startDate: p.startDate,
      endDate: p.endDate,
      initialPastureCoverKgHa: 0,
      targetResidualKgHa: 0,
      grazingMonths: [],
      supplementSubstitutionRates: DEFAULT_SUPPLEMENT_SUBSTITUTION_RATES,
      animalGroupBudgets: [],
    })),
    /** The "starred"/default plan shown in the planner card */
    activePlanId,
    setActivePlanId,
    /** The plan currently being viewed (may differ from activePlanId) */
    viewedPlanId,
    setViewedPlanId,
    createPlan,
    duplicatePlan,
    deletePlan,
    updatePlan,
    updatePlanMetadata,
    addStockClassToGroup,
    removeStockClassFromGroup,
    addSupplementFeedType,
    removeSupplementFeedType,
    isLoading,
    isSaving,
    isDirty: metadataDirty || dirtyGroups.size > 0,
    metadataDirty,
    dirtyGroups,
    discardGroupChanges,
    saveError,
    savePlan,
  };
}
